<template>
  <div class="audioRecorder">
    <div class="recorderStatus" :class="{ recording: isRecording }">
      <span class="statusDot"></span>
      <strong>{{ isRecording ? "Gravando" : "Gravação de áudio" }}</strong>
      <span class="timer">{{ formattedDuration }}</span>
    </div>

    <div class="recorderActions">
      <button
        v-if="!isRecording && !audioBlob"
        type="button"
        class="audioButton primary"
        :disabled="isBusy"
        @click="startRecording"
      >
        🎙 Iniciar gravação
      </button>
      <button
        v-if="isRecording"
        type="button"
        class="audioButton stop"
        @click="stopRecording"
      >
        ■ Parar gravação
      </button>
    </div>

    <div v-if="audioUrl" class="audioPreview">
      <AudioPlayer :src="audioUrl" :duration-seconds="durationSeconds" />
      <div class="previewActions">
        <button
          type="button"
          class="audioButton secondary"
          :disabled="isBusy"
          @click="discardRecording"
        >
          {{ isUploaded ? "Nova gravação" : "Descartar / regravar" }}
        </button>
        <button
          v-if="!isUploaded"
          type="button"
          class="audioButton primary"
          :disabled="isBusy"
          @click="confirmUpload"
        >
          {{ isUploading ? "Enviando..." : "Confirmar e enviar" }}
        </button>
      </div>
    </div>

    <div v-if="isUploading" class="processingMessage">
      <span class="spinner"></span>
      Enviando áudio para armazenamento seguro...
    </div>

    <p v-if="errorMessage" class="audioError">{{ errorMessage }}</p>

    <!-- TRANSCRIÇÃO EM STANDBY
    <div v-if="transcriptionText && !isUploaded" class="latestTranscription">
      <label>Texto transcrito</label>
      <textarea v-model="transcriptionText" rows="3"></textarea>
    </div>
    -->

    <div v-if="recordings.length" class="savedRecordings">
      <div
        v-for="recording in recordings"
        :key="recording.id || recording.key"
        class="savedRecording"
      >
        <div class="savedRecordingHeader">
          <strong>🎧 {{ recording.name || "Áudio da inspeção" }}</strong>
          <button
            type="button"
            class="removeRecording"
            title="Remover áudio"
            :disabled="isDeleting(recording)"
            @click="deleteRecording(recording)"
          >
            {{ isDeleting(recording) ? "…" : "×" }}
          </button>
        </div>
        <AudioPlayer
          v-if="recording.url"
          :src="recording.url"
          :duration-seconds="Number(recording.durationSeconds || 0)"
        />
        <small v-else
          >O player estará disponível após atualizar a inspeção.</small
        >
        <!-- TRANSCRIÇÃO EM STANDBY
        <div class="transcriptionHeader">
          <label>Transcrição</label>
          <span
            v-if="recording.transcriptionStatus === 'COMPLETED'"
            class="statusBadge success"
            >Concluída</span
          >
          <span
            v-else-if="recording.transcriptionStatus === 'IN_PROGRESS'"
            class="statusBadge processing"
            >Processando</span
          >
          <span
            v-else-if="recording.transcriptionStatus === 'FAILED'"
            class="statusBadge failed"
            >Falhou</span
          >
        </div>
        <textarea
          v-if="recording.transcription"
          :value="recording.transcription"
          rows="3"
          @input="
            $emit('transcription-change', {
              recording,
              value: $event.target.value,
            })
          "
        ></textarea>
        <div v-else class="emptyTranscription">
          <span>Nenhuma transcrição disponível.</span>
          <button
            type="button"
            class="retryButton"
            :disabled="isRetrying(recording)"
            @click="retryTranscription(recording)"
          >
            {{
              isRetrying(recording)
                ? "Transcrevendo..."
                : "Tentar novamente"
            }}
          </button>
        </div>
        <small v-if="recordingErrors[recording.key]" class="audioError">
          {{ recordingErrors[recording.key] }}
        </small>
        -->
        <small v-if="recordingErrors[recording.key]" class="audioError">
          {{ recordingErrors[recording.key] }}
        </small>
      </div>
    </div>
  </div>
</template>

<script>
import serviceInspectionAudio from "../services/serviceInspectionAudio";
import AudioPlayer from "./AudioPlayer.vue";

const MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
  "audio/mp4",
];

export default {
  name: "AudioRecorder",
  components: { AudioPlayer },
  props: {
    recordings: {
      type: Array,
      default: () => [],
    },
    maxDurationSeconds: {
      type: Number,
      default: 300,
    },
    inspectionId: {
      type: String,
      default: "",
    },
    /* TRANSCRIÇÃO EM STANDBY
    transcriptionContext: {
      type: Object,
      default: () => ({}),
    },
    */
  },
  data() {
    return {
      isRecording: false,
      isUploading: false,
      // TRANSCRIÇÃO EM STANDBY
      // isTranscribing: false,
      // transcriptionText: "",
      errorMessage: "",
      durationSeconds: 0,
      audioBlob: null,
      audioUrl: "",
      mediaRecorder: null,
      mediaStream: null,
      chunks: [],
      timerId: null,
      recordingStartedAt: null,
      isUploaded: false,
      // destroyed: false,
      // retryingRecordings: {},
      recordingErrors: {},
      deletingRecordings: {},
    };
  },
  computed: {
    isBusy() {
      return this.isUploading;
    },
    formattedDuration() {
      const minutes = Math.floor(this.durationSeconds / 60);
      const seconds = this.durationSeconds % 60;
      return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
        2,
        "0"
      )}`;
    },
  },
  /* TRANSCRIÇÃO EM STANDBY
  watch: {
    "recordings.length": {
      immediate: true,
      handler() {
        this.resumePendingTranscriptions();
      },
    },
  },
  */
  beforeDestroy() {
    this.cleanupRecorder();
    this.revokeAudioUrl();
  },
  methods: {
    async startRecording() {
      this.errorMessage = "";
      this.isUploaded = false;

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.errorMessage = "Este navegador não oferece acesso ao microfone.";
        return;
      }
      if (typeof MediaRecorder === "undefined") {
        this.errorMessage =
          "A gravação de áudio não é suportada neste navegador.";
        return;
      }

      try {
        this.discardRecording();
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
        const mimeType =
          typeof MediaRecorder.isTypeSupported === "function"
            ? MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type))
            : null;
        this.mediaRecorder = mimeType
          ? new MediaRecorder(this.mediaStream, { mimeType })
          : new MediaRecorder(this.mediaStream);
        this.chunks = [];
        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) this.chunks.push(event.data);
        };
        this.mediaRecorder.onstop = this.finishRecording;
        this.mediaRecorder.onerror = () => {
          this.errorMessage = "A gravação foi interrompida inesperadamente.";
          this.cleanupRecorder();
        };
        this.mediaRecorder.start(1000);
        this.isRecording = true;
        this.durationSeconds = 0;
        this.recordingStartedAt = Date.now();
        this.startTimer();
      } catch (error) {
        this.errorMessage = this.microphoneErrorMessage(error);
        this.cleanupRecorder();
      }
    },
    stopRecording() {
      if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
        this.mediaRecorder.stop();
      }
      this.isRecording = false;
      this.stopTimer();
    },
    finishRecording() {
      const mimeType =
        this.mediaRecorder?.mimeType || this.chunks[0]?.type || "audio/webm";
      const blob = new Blob(this.chunks, { type: mimeType });
      this.cleanupStream();
      if (!blob.size) {
        this.errorMessage =
          "Nenhum áudio foi capturado. Tente gravar novamente.";
        return;
      }
      this.audioBlob = blob;
      this.revokeAudioUrl();
      this.audioUrl = URL.createObjectURL(blob);
    },
    async confirmUpload() {
      if (!this.audioBlob || this.isBusy) return;
      this.errorMessage = "";
      this.isUploading = true;

      try {
        const extension = this.extensionForMime(this.audioBlob.type);
        const file = new File(
          [this.audioBlob],
          `inspecao-audio-${Date.now()}.${extension}`,
          { type: this.audioBlob.type.split(";")[0] }
        );
        const upload = await serviceInspectionAudio.uploadAudio(
          file,
          this.durationSeconds
        );
        this.isUploading = false;
        this.isUploaded = true;

        this.$emit("completed", {
          ...upload.audio,
          type: "audio",
          date: new Date().toISOString(),
        });
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Não foi possível enviar o áudio.";
      } finally {
        this.isUploading = false;
      }
    },
    /* TRANSCRIÇÃO EM STANDBY
    async waitForTranscription(jobId) {
      for (let attempt = 0; attempt < 120 && !this.destroyed; attempt += 1) {
        const result = await serviceInspectionAudio.getTranscription(jobId);
        if (["COMPLETED", "FAILED"].includes(result.status)) return result;
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
      return { status: "IN_PROGRESS", transcriptionText: "" };
    },
    async resumePendingTranscriptions() {
      const pending = this.recordings.filter(
        (recording) =>
          recording.transcriptionJobId &&
          ["QUEUED", "IN_PROGRESS"].includes(recording.transcriptionStatus)
      );
      await Promise.all(
        pending.map(async (recording) => {
          try {
            const result = await this.waitForTranscription(
              recording.transcriptionJobId
            );
            if (["COMPLETED", "FAILED"].includes(result.status)) {
              this.$emit("transcription-result", { recording, result });
            }
          } catch (_) {
            // The saved audio remains available even if status refresh fails.
          }
        })
      );
    },
    async retryTranscription(recording) {
      if (!recording?.key || this.isRetrying(recording)) return;
      this.$set(this.retryingRecordings, recording.key, true);
      this.$set(this.recordingErrors, recording.key, "");

      try {
        const retry = await serviceInspectionAudio.retryTranscription(
          recording.key,
          recording.mimeType || this.mimeTypeForName(recording.name),
          this.transcriptionContext
        );
        const result = retry.transcription.status === "COMPLETED"
          ? retry.transcription
          : await this.waitForTranscription(retry.transcription.jobId);
        this.$emit("transcription-result", {
          recording,
          result: { ...result, jobId: retry.transcription.jobId },
        });

        if (result.status === "FAILED") {
          this.$set(
            this.recordingErrors,
            recording.key,
            result.errorMessage || "Não foi possível transcrever este áudio."
          );
        } else if (result.status !== "COMPLETED") {
          this.$set(
            this.recordingErrors,
            recording.key,
            "A transcrição continua sendo processada."
          );
        }
      } catch (error) {
        this.$set(
          this.recordingErrors,
          recording.key,
          error?.response?.data?.message ||
            "Não foi possível tentar a transcrição novamente."
        );
      } finally {
        this.$set(this.retryingRecordings, recording.key, false);
      }
    },
    */
    async deleteRecording(recording) {
      if (!recording?.key || this.isDeleting(recording)) return;
      const message =
        "Excluir este áudio? O arquivo será removido permanentemente do armazenamento.";
      const confirmed = this.$bvModal
        ? await this.$bvModal.msgBoxConfirm(message, {
            title: "Excluir áudio",
            okTitle: "Excluir",
            cancelTitle: "Cancelar",
            okVariant: "danger",
            centered: true,
          })
        : window.confirm(message);
      if (!confirmed) return;

      this.$set(this.deletingRecordings, recording.key, true);
      this.$set(this.recordingErrors, recording.key, "");
      try {
        await serviceInspectionAudio.deleteAudio(
          recording.key,
          this.inspectionId
        );
        this.$emit("remove", recording);
      } catch (error) {
        this.$set(
          this.recordingErrors,
          recording.key,
          error?.response?.data?.message ||
            "Não foi possível excluir o áudio do armazenamento."
        );
      } finally {
        this.$set(this.deletingRecordings, recording.key, false);
      }
    },
    isDeleting(recording) {
      return Boolean(this.deletingRecordings[recording?.key]);
    },
    /* TRANSCRIÇÃO EM STANDBY
    isRetrying(recording) {
      return Boolean(
        this.retryingRecordings[recording?.key] ||
          ["QUEUED", "IN_PROGRESS"].includes(recording?.transcriptionStatus)
      );
    },
    mimeTypeForName(name) {
      const extension = String(name || "")
        .split(".")
        .pop()
        .toLowerCase();
      return (
        {
          ogg: "audio/ogg",
          mp4: "audio/mp4",
          m4a: "audio/m4a",
          mp3: "audio/mpeg",
          wav: "audio/wav",
        }[extension] || "audio/webm"
      );
    },
    */
    discardRecording() {
      if (this.isRecording) this.stopRecording();
      this.revokeAudioUrl();
      this.audioBlob = null;
      this.chunks = [];
      this.durationSeconds = 0;
      this.errorMessage = "";
      this.isUploaded = false;
    },
    startTimer() {
      this.stopTimer();
      this.timerId = window.setInterval(() => {
        this.durationSeconds = Math.floor(
          (Date.now() - this.recordingStartedAt) / 1000
        );
        if (this.durationSeconds >= this.maxDurationSeconds)
          this.stopRecording();
      }, 250);
    },
    stopTimer() {
      if (this.timerId) window.clearInterval(this.timerId);
      this.timerId = null;
    },
    cleanupStream() {
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach((track) => track.stop());
      }
      this.mediaStream = null;
    },
    cleanupRecorder() {
      this.stopTimer();
      if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
        this.mediaRecorder.onstop = null;
        this.mediaRecorder.stop();
      }
      this.mediaRecorder = null;
      this.isRecording = false;
      this.cleanupStream();
    },
    revokeAudioUrl() {
      if (this.audioUrl) URL.revokeObjectURL(this.audioUrl);
      this.audioUrl = "";
    },
    extensionForMime(mimeType) {
      const normalized = String(mimeType).split(";")[0];
      return (
        {
          "audio/ogg": "ogg",
          "audio/mp4": "mp4",
          "audio/mpeg": "mp3",
          "audio/wav": "wav",
        }[normalized] || "webm"
      );
    },
    microphoneErrorMessage(error) {
      if (["NotAllowedError", "SecurityError"].includes(error?.name)) {
        return "Permissão de microfone negada. Autorize o acesso nas configurações do navegador.";
      }
      if (error?.name === "NotFoundError")
        return "Nenhum microfone foi encontrado.";
      if (error?.name === "NotReadableError")
        return "O microfone está sendo utilizado por outro aplicativo.";
      return "Não foi possível acessar o microfone.";
    },
  },
};
</script>

<style scoped>
.audioRecorder {
  padding: 12px;
  border: 1px solid #dce9e5;
  border-radius: 8px;
  background: #f8fbfa;
}
.recorderStatus,
.savedRecordingHeader,
.previewActions,
.recorderActions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.recorderStatus {
  color: #415a55;
  margin-bottom: 9px;
  font-size: 11px;
}
.recorderStatus.recording {
  color: #b42318;
}
.statusDot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #9ca3af;
}
.recording .statusDot {
  background: #d92d20;
  animation: pulse 1s infinite;
}
.timer {
  margin-left: auto;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  font-size: 11px;
}
.audioPreview,
.savedRecording {
  margin-top: 10px;
}
.previewActions {
  flex-wrap: wrap;
  margin-top: 10px;
}
.audioButton {
  border: 0;
  border-radius: 5px;
  padding: 6px 10px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
}
.audioButton:disabled {
  opacity: 0.6;
  cursor: wait;
}
.audioButton.primary {
  background: #006256;
  color: #fff;
}
.audioButton.secondary {
  background: #e4ecea;
  color: #29443e;
}
.audioButton.stop {
  background: #b42318;
  color: #fff;
}
.processingMessage {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 9px;
  color: #006256;
  font-size: 11px;
}
.spinner {
  width: 15px;
  height: 15px;
  border: 2px solid #b8d6d0;
  border-top-color: #006256;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
.audioError {
  display: block;
  color: #b42318;
  margin: 8px 0 0;
  font-size: 10px;
}
.latestTranscription,
.savedRecording {
  padding: 10px;
  border-radius: 6px;
  background: #fff;
  border: 1px solid #e1e8e6;
}
.latestTranscription {
  margin-top: 12px;
}
.latestTranscription label,
.savedRecording label {
  display: block;
  margin: 7px 0 4px;
  font-size: 10px;
  font-weight: 700;
  color: #425c56;
}
textarea {
  width: 100%;
  resize: vertical;
  border: 1px solid #ccd9d6;
  border-radius: 5px;
  padding: 7px;
  font-size: 11px;
  line-height: 1.45;
}
.savedRecordingHeader {
  justify-content: space-between;
  margin-bottom: 8px;
}
.savedRecordingHeader strong {
  color: #304943;
  font-size: 11px;
  font-weight: 600;
}
.removeRecording {
  border: 0;
  background: transparent;
  color: #b42318;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
}
.removeRecording:disabled {
  opacity: 0.55;
  cursor: wait;
}
.transcriptionHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 4px;
}
.statusBadge {
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 9px;
  font-weight: 700;
}
.statusBadge.success {
  color: #126345;
  background: #e3f3ec;
}
.statusBadge.processing {
  color: #855b00;
  background: #fff4d6;
}
.statusBadge.failed {
  color: #a12a20;
  background: #fdeae7;
}
.emptyTranscription {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px;
  border: 1px dashed #ccd9d6;
  border-radius: 5px;
  color: #687b77;
  font-size: 10px;
}
.retryButton {
  flex: 0 0 auto;
  padding: 4px 8px;
  border: 1px solid #006256;
  border-radius: 4px;
  color: #006256;
  background: #fff;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
}
.retryButton:disabled {
  opacity: 0.6;
  cursor: wait;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
@keyframes pulse {
  50% {
    opacity: 0.35;
  }
}
</style>
